/** These question-settings (QuestionConstantT) should be moved into the question modules themselves after it's been double checked which of it's props is actually used(!) */
import type { QuestionType } from '@mentimeter/http-clients';
import keyBy from 'lodash/keyBy';
import type { MusicCollection, QuestionConstant } from '../types';

const QUIZ_MUSIC_URL = 'https://static.mentimeter.com/static/music';
export const QUIZ_MUSIC: MusicCollection = [
  {
    artist: 'Joe E. Lee',
    title: 'A One Way Ticket Too Much',
    url: `${QUIZ_MUSIC_URL}/a_one_way_ticket_too_much.mp3`,
  },
  {
    artist: 'Gary Combs',
    title: 'Ladies on the Loose',
    url: `${QUIZ_MUSIC_URL}/ladies_on_the_loose.mp3`,
  },
  {
    artist: 'Birdies',
    title: 'Jumping Cricket',
    url: `${QUIZ_MUSIC_URL}/jumping_cricket.mp3`,
  },
];

const questionTypeChoices: QuestionConstant = {
  type: 'choices',
  name: 'Multiple Choice',
  moduleId: 'choices',
  icon: 'multiple-q',
};

const questionTypeWordCloud: QuestionConstant = {
  type: 'wordcloud',
  moduleId: 'wordcloud',
  name: 'Word Cloud',
  icon: 'word-cloud',
};

const questionTypeOpen: QuestionConstant = {
  type: 'open',
  moduleId: 'open',
  name: 'Open Ended',
  icon: 'comments',
};

const questionTypeScales: QuestionConstant = {
  type: 'scales',
  moduleId: 'scales',
  name: 'Scales',
  icon: 'scale-q',
};

const questionTypeRanking: QuestionConstant = {
  type: 'ranking',
  moduleId: 'ranking',
  name: 'Ranking',
  icon: 'ranking-q',
};

const questionTypeQfa: QuestionConstant = {
  type: 'qfa',
  moduleId: 'qfa',
  name: 'Q&A',
  icon: 'qfa',
  // unrestricted means available on free plan within question limit
  unrestricted: true,
};

const questionTypePrioritisation: QuestionConstant = {
  type: 'prioritisation',
  moduleId: '100-points',
  name: '100 points',
  icon: 'prio-q',
};

const questionTypeRating: QuestionConstant = {
  type: 'rating',
  moduleId: 'rating',
  name: '2 x 2 Grid',
  icon: 'rating-box',
};

const questionTypePinOnImage: QuestionConstant = {
  type: 'pin_on_image',
  moduleId: 'pin-on-image',
  icon: 'pin-on-image',
  name: 'Pin on Image',
};

const questionTypeMetadata: QuestionConstant = {
  type: 'metadata',
  moduleId: 'metadata',
  name: 'Quick Form',
  icon: 'metadata',
  pro: true,
};

const slideTypeBullets: QuestionConstant = {
  type: 'slide',
  moduleId: 'bullets',
  unrestricted: true,
  slideType: 'bullets',
  icon: 'heading-bullets',
  name: 'Bullets',
};

const slideTypeBig: QuestionConstant = {
  type: 'slide',
  moduleId: 'big',
  unrestricted: true,
  slideType: 'big',
  icon: 'big',
  name: 'Big',
};

export const INTERACTIVE_SLIDES_TYPES: Array<QuestionConstant> = [
  questionTypeChoices,
  questionTypeWordCloud,
  questionTypeOpen,
  questionTypeScales,
  questionTypeRanking,
  questionTypeQfa,
  {
    moduleId: 'numerical-question',
    name: 'Guess the Number',
    type: 'scales',
  },
  questionTypePrioritisation,
  questionTypeRating,
  questionTypeMetadata,
  questionTypePinOnImage,
];

export const INTERACTIVE_QUESTION_TYPES: Array<QuestionConstant> = [
  ...INTERACTIVE_SLIDES_TYPES,
];

export const QUIZ_OPEN_QUESTION_TYPE: QuestionConstant = {
  type: 'quiz_open',
  moduleId: 'quiz-open',
  name: 'Type Answer',
  icon: 'quizOe',
};

export const QUIZ_QUESTION_TYPE: QuestionConstant = {
  type: 'quiz',
  moduleId: 'quiz-choices',
  name: 'Select Answer',
  icon: 'quiz',
};

export const QUIZ_LEADERBOARD_TYPE: QuestionConstant = {
  type: 'quiz_leaderboard',
  moduleId: 'quiz-leaderboard',
  name: 'Leaderboard',
  icon: 'leaderboard',
  unrestricted: true,
};

export const RESTRICTED_INTEGRATION_SLIDES_TYPES: QuestionConstant[] = [
  {
    type: 'slide',
    moduleId: 'google-slides',
    name: 'Google slides',
    icon: 'powerpoint-slide',
  },
  {
    type: 'slide',
    moduleId: 'powerpoint',
    name: 'Powerpoint',
    icon: 'powerpoint-slide',
  },
];

export const INTEGRATION_SLIDES_TYPES: QuestionConstant[] = [
  ...RESTRICTED_INTEGRATION_SLIDES_TYPES,
  {
    type: 'slide',
    moduleId: 'miro',
    name: 'Miro',
    icon: 'powerpoint-slide',
    unrestricted: true,
  },
];

export const CONTENT_SLIDES_TYPES: Array<QuestionConstant> = [
  {
    type: 'slide',
    moduleId: 'free-text',
    unrestricted: true,
    slideType: 'free-text',
    icon: 'heading-subheading',
    name: 'Text',
  },
  {
    type: 'slide',
    moduleId: 'heading',
    unrestricted: true,
    slideType: 'heading',
    icon: 'heading-subheading',
    name: 'Heading',
  },
  {
    type: 'slide',
    moduleId: 'paragraph',
    unrestricted: true,
    slideType: 'paragraph',
    icon: 'heading-paragraph',
    name: 'Paragraph',
  },
  slideTypeBullets,
  {
    type: 'slide',
    moduleId: 'image',
    unrestricted: true,
    slideType: 'image',
    icon: 'picture-o',
    name: 'Image',
  },
  {
    type: 'slide',
    moduleId: 'video',
    icon: 'video',
    unrestricted: true,
    slideType: 'video',
    name: 'Video',
  },
  slideTypeBig,
  {
    type: 'slide',
    moduleId: 'quote',
    unrestricted: true,
    slideType: 'quote',
    icon: 'quote',
    name: 'Quote',
  },
  {
    type: 'slide',
    moduleId: 'number',
    unrestricted: true,
    slideType: 'number',
    icon: 'number',
    name: 'Number',
  },
  {
    type: 'slide',
    moduleId: 'instructions',
    unrestricted: true,
    slideType: 'instructions',
    icon: 'instructions',
    name: 'Instructions',
  },
  {
    type: 'slide',
    moduleId: 'document',
    unrestricted: true,
    slideType: 'document',
    icon: 'powerpoint-slide',
    name: 'Document',
  },
];

export const QUIZ_OPEN_QUESTION_TYPES = [
  QUIZ_QUESTION_TYPE,
  QUIZ_OPEN_QUESTION_TYPE,
  QUIZ_LEADERBOARD_TYPE,
];

export const QUIZ_QUESTION_TYPES = [QUIZ_QUESTION_TYPE, QUIZ_LEADERBOARD_TYPE];

export const ALL_QUIZ_QUESTION_TYPES = [
  QUIZ_QUESTION_TYPE,
  QUIZ_OPEN_QUESTION_TYPE,
];

export const ALL_QUIZ_SLIDE_TYPES = [
  ...ALL_QUIZ_QUESTION_TYPES,
  QUIZ_LEADERBOARD_TYPE,
];

export const ALL_QUESTION_TYPES = [
  ...INTERACTIVE_SLIDES_TYPES,
  ...QUIZ_OPEN_QUESTION_TYPES,
  ...QUIZ_QUESTION_TYPES,
  ...CONTENT_SLIDES_TYPES,
  ...INTEGRATION_SLIDES_TYPES,
];

export const QUIZ_QUESTIONS_TYPES: QuestionType[] = ['quiz', 'quiz_open'];
export const QUIZ_SLIDES_TYPES: QuestionType[] = [
  ...QUIZ_QUESTIONS_TYPES,
  'quiz_leaderboard',
];

export const ALL_QUESTION_TYPES_BY_TYPE = keyBy<QuestionConstant>(
  ALL_QUESTION_TYPES,
  'type',
);

export const MAXIMUM_NUMBER_OF_SLIDES_MESSAGE =
  'You have reached the maximum number of slides.';

export const CONTENT_SLIDE_MODULE_IDS = CONTENT_SLIDES_TYPES.map(
  (slide) => slide.moduleId,
);
